import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useCurrentUser, useInvalidateAuthentication } from 'api/authentication/hooks';
import { StepKey } from 'api/authentication/requests';
import { handleError } from 'api/query-client';

import {
  addAzureADSettings,
  completeOnboarding,
  completeOnboardingStep,
  completePreboarding,
  deleteEmailSettings,
  editBillingDetails,
  editCompanyDetails,
  editEmailSettings,
  loadAzureADSettings,
  loadAzureADSettingsForEmail,
  loadBillingDetails,
  loadChargebeePaymentMethodPage,
  loadChargebeePlanPages,
  loadCompanyDetails,
  loadEmailSettings,
  loadInvoicePdf,
  loadInvoices,
  loadTimezones,
  removeAzureADSettings,
  skipOnboarding,
  verifyEmailSettings,
} from './requests';

export const useCompanyDetails = () => {
  return useQuery({
    queryKey: ['company-details'],
    queryFn: () => loadCompanyDetails(),
  });
};

type EditCompanyDetailsProps = {
  onSuccessText?: string;
  onSuccess?: () => void;
  shouldNotShowSuccess?: boolean;
};

export const useEditCompanyDetails = (props: EditCompanyDetailsProps = {}) => {
  const { onSuccessText, onSuccess, shouldNotShowSuccess } = props;
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  return useMutation({
    mutationFn: editCompanyDetails,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(['company-details']),
        invalidateCurrentUser(),
      ]);
      !shouldNotShowSuccess && showSuccess(onSuccessText ?? t('companyUpdated'));
      setTimeout(() => {
        onSuccess?.();
      });
    },
    onError: () => {
      handleError(t('companyNotUpdated'));
    },
  });
};

export const useCompletePreboarding = () => {
  const queryClient = useQueryClient();
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  return useMutation({
    mutationFn: completePreboarding,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['company-details']);
      invalidateCurrentUser();
    },
  });
};

export const useSkipOnboarding = () => {
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  return useMutation({
    mutationFn: skipOnboarding,
    onSuccess: invalidateCurrentUser,
  });
};

export const useCompleteOnboarding = () => {
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  return useMutation({
    mutationFn: completeOnboarding,
    onSuccess: invalidateCurrentUser,
  });
};

export const useCompleteOnboardingStep = () => {
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const currentUser = useCurrentUser();
  const mutation = useMutation({
    mutationFn: completeOnboardingStep,
    onSuccess: () => {
      invalidateCurrentUser();
    },
  });
  return (step: StepKey, travellerJourneyShortId?: string) => {
    const onboardingStatus = currentUser?.company.onboarding?.status;
    const isComplete = onboardingStatus?.steps[step];
    const isCorrectTravellerJourney = travellerJourneyShortId
      ? onboardingStatus?.config.travellerJourneyId === travellerJourneyShortId
      : true;
    if (!isComplete && isCorrectTravellerJourney) {
      mutation.mutate(step);
    }
  };
};

export const useCompanyTimezones = () => {
  return useQuery({
    queryKey: ['company-default-timezone-settings'],
    queryFn: loadTimezones,
  });
};

export const useEmailSettings = () => {
  return useQuery({
    queryKey: ['company-email-settings'],
    queryFn: loadEmailSettings,
  });
};

type EmailSettingsError = AxiosError<{ domain: string }>;

export const useEditEmailSettings = () => {
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: editEmailSettings,
    onError: (error: EmailSettingsError) => {
      handleError(error.response?.data?.domain ?? t('emailSettingsUnknownError'));
    },
  });
};

export const useRemoveEmailSettings = () => {
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: deleteEmailSettings,
    onSuccess: () => {
      showSuccess(t('emailSettingsRemoved'));
    },
  });
};

export const useVerifyEmailSettings = () => {
  return useMutation({ mutationFn: verifyEmailSettings });
};

export const useAzureAdSettingsForEmail = (email?: string) => {
  return useQuery({
    queryKey: ['company-azure-ad-settings-for-email'],
    queryFn: () => loadAzureADSettingsForEmail(email),
    enabled: false,
  });
};

export const useAzureAdSettings = () => {
  return useQuery({
    queryKey: ['company-azure-ad-settings'],
    queryFn: loadAzureADSettings,
  });
};

export const useAddAzureAdSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addAzureADSettings,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['company-azure-ad-settings']);
    },
  });
};

export const useRemoveAzureAdSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeAzureADSettings,
    onSuccess: () => {
      queryClient.invalidateQueries(['company-azure-ad-settings']);
    },
  });
};

export const useBillingDetails = () => {
  return useQuery({
    queryKey: ['company-billing-details'],
    queryFn: () => loadBillingDetails(),
  });
};

type UpdateBillingError = AxiosError<{ error: string }>;

export const useUpdateBillingInfo = () => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editBillingDetails,
    onSuccess: async () => {
      showSuccess(t('billingDetailsUpdated'));
      queryClient.invalidateQueries(['company-billing-details']);
    },
    onError: (error: UpdateBillingError) => {
      handleError(error.response?.data?.error ?? t('billingDetailsNotUpdated'));
    },
  });
};

export const useListInvoices = () => {
  return useQuery({
    queryKey: ['company-list-invoices'],
    queryFn: () => loadInvoices(),
  });
};

export const useGetInvoicePdf = (invoiceId: string) => {
  return useQuery({
    queryKey: ['company-invoice-pdf', invoiceId],
    queryFn: () => loadInvoicePdf(invoiceId),
  });
};

export const useGetChargeBeePaymentMethodPage = () => {
  return useQuery({
    queryKey: ['chargebee-payment-page'],
    queryFn: () => loadChargebeePaymentMethodPage(),
  });
};

export const useGetChargeBeePlanPage = () => {
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: loadChargebeePlanPages,
    onError: () => {
      handleError(t('plansPageError'));
    },
  });
};

export const locale = {
  en: {
    companyUpdated: 'Company details updated',
    companyNotUpdated: 'Company details not updated',
    emailSettingsUnknownError: 'Unknown error',
    emailSettingsRemoved: 'Email settings removed',
    portalDomainEditError: 'Domain already registered, please use a unique domain',
    portalDomainEditSuccess:
      'Domain saved. Please follow the instructions on the page to set up the DNS.',
    billingDetailsUpdated: 'Billing details updated',
    billingDetailsNotUpdated: 'Billing details not updated, an error occurred',
    plansPageError: 'Error loading checkout page',
  },
};
